import { BaseComponent } from "utils/base-component"
import "./index.scss"

require.context("./static", true, /\.svg|jpg$/)

class Accordion {
  public rootEl: HTMLElement
  private buttonEl: HTMLButtonElement
  private contentEl: HTMLElement
  private open: boolean
  private measuredHeight: number | null = null

  constructor(domNode: HTMLElement) {
    this.rootEl = domNode
    this.buttonEl = this.rootEl.querySelector(
      `button.accordion-reviews__accordion-button`,
    ) as HTMLButtonElement

    const controlsId = this.buttonEl.getAttribute("aria-controls")
    this.contentEl = document.getElementById(controlsId!) as HTMLElement

    this.open = this.buttonEl.getAttribute("aria-expanded") === "true"

    // add event listeners
    this.buttonEl.addEventListener("click", this.onButtonClick.bind(this))
  }

  private onButtonClick() {
    this.toggle(!this.open)
  }

  private toggle(open: boolean) {
    // don't do anything if the open state doesn't change
    if (open === this.open) {
      return
    }

    // update the internal state
    this.open = open

    // handle DOM updates - CSS handles the styling based on aria attributes
    this.buttonEl.setAttribute("aria-expanded", `${open}`)
    this.contentEl.setAttribute("aria-hidden", `${!open}`)
  }

  // Add public open and close methods for convenience
  public openAccordion() {
    this.toggle(true)
  }

  public closeAccordion() {
    this.toggle(false)
  }

  // Measure the height of the panel content when expanded
  public measureHeight(): number {
    if (this.measuredHeight !== null) {
      return this.measuredHeight
    }

    // Temporarily expand the panel to measure its height
    const wasOpen = this.open
    if (!wasOpen) {
      this.toggle(true)
    }

    // Get the natural height of the content
    const height = this.contentEl.scrollHeight
    this.measuredHeight = height

    // Restore original state if it was closed
    if (!wasOpen) {
      this.toggle(false)
    }

    return height
  }

  // Get the measured height without re-measuring
  public getMeasuredHeight(): number | null {
    return this.measuredHeight
  }
}

export class AccordionReviews extends BaseComponent {
  static cssClass = "accordion-reviews"

  private accordions: Accordion[] = []

  init() {
    this.accordions = []
    this.initializeAccordions()
    this.autoExpandShortestPanels()
  }

  private initializeAccordions() {
    const accordionElements = this.root.querySelectorAll(`.${this.cssClass}__accordion`)

    accordionElements.forEach((accordionEl) => {
      const accordion = new Accordion(accordionEl as HTMLElement)
      this.accordions.push(accordion)
    })
  }

  private autoExpandShortestPanels() {
    // Group accordions by column
    const columns = this.groupAccordionsByColumn()

    // Process each column to get height data
    const columnResults = columns
      .map((columnAccordions, columnIndex) => {
        if (columnAccordions.length === 0) return null

        // Measure all panels in the column
        const heights = columnAccordions.map((accordion, index) => ({
          accordion,
          height: accordion.measureHeight(),
          index,
        }))

        // Sort by height to get shortest and second shortest
        heights.sort((a, b) => a.height - b.height)

        return {
          columnIndex,
          accordions: columnAccordions,
          heights,
          shortest: heights[0],
          secondShortest: heights[1] || null,
        }
      })
      .filter((result) => result !== null)

    // Check if both shortest panels are the last in their columns
    const bothLastPanels = columnResults.every(
      (result) => result!.shortest.index === result!.accordions.length - 1,
    )

    if (bothLastPanels && columnResults.length >= 2) {
      // Choose second shortest in the first column for better balance
      columnResults[0]!.secondShortest?.accordion.openAccordion()
      columnResults[1]!.shortest.accordion.openAccordion()

      // Handle any additional columns normally
      for (let i = 2; i < columnResults.length; i += 1) {
        columnResults[i]!.shortest.accordion.openAccordion()
      }
    } else {
      // Use normal logic - expand shortest in each column
      columnResults.forEach((result) => {
        result!.shortest.accordion.openAccordion()
      })
    }
  }

  private groupAccordionsByColumn(): Accordion[][] {
    const columns: Accordion[][] = []
    const columnElements = this.root.querySelectorAll(`.${this.cssClass}__accordion-column`)

    columnElements.forEach((columnEl) => {
      const columnAccordions: Accordion[] = []
      const accordionElements = columnEl.querySelectorAll(`.${this.cssClass}__accordion`)

      accordionElements.forEach((accordionEl) => {
        // Find the corresponding Accordion instance
        const accordion = this.accordions.find((acc) => acc.rootEl === accordionEl)
        if (accordion) {
          columnAccordions.push(accordion)
        }
      })

      columns.push(columnAccordions)
    })

    return columns
  }
}
